// HTTP + SSE client for one device token against a DigiCalls backend.

const FALLBACK_ICE: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

export type BrokerEvent = {
  type: string;
  sessionId?: string;
  id?: string;
  peer?: string;
  status?: string;
  reason?: string;
  /** Present on `call-peer-mute` — remote party muted their microphone. */
  muted?: boolean;
  [k: string]: unknown;
};

/** Handle for an SSE subscription. Call close() to stop it (and its retries). */
export type StreamHandle = { close(): void };

const SSE_MAX_BACKOFF_MS = 30_000;
const SSE_STALE_AFTER_MS = 45_000;
const SSE_STALE_CHECK_MS = 15_000;

// ReconnectingStream wraps EventSource with exponential-backoff reconnection and
// half-open detection (via the server's heartbeat), so an integrator's call
// session survives transient network drops without the stream silently dying.
// The backend resends a full snapshot on every new connection, so reconnecting
// also resyncs state.
class ReconnectingStream implements StreamHandle {
  private es: EventSource | null = null;
  private retry = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private staleTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivity = 0;
  private closed = false;

  constructor(
    private readonly url: string,
    private readonly onEvent: (ev: BrokerEvent) => void,
  ) {
    this.open();
    this.staleTimer = setInterval(() => {
      if (this.es && Date.now() - this.lastActivity > SSE_STALE_AFTER_MS) this.reopenSoon();
    }, SSE_STALE_CHECK_MS);
  }

  private open(): void {
    if (this.es || this.closed) return;
    const es = new EventSource(this.url);
    this.es = es;
    this.lastActivity = Date.now();
    es.onopen = () => {
      this.retry = 0;
      this.lastActivity = Date.now();
    };
    es.onmessage = (e) => {
      this.lastActivity = Date.now();
      try {
        const ev = JSON.parse(e.data) as BrokerEvent;
        if (ev.type === "ping") return; // heartbeat
        this.onEvent(ev);
      } catch {
        /* ignore keepalives / malformed */
      }
    };
    es.onerror = () => this.reopenSoon();
  }

  private reopenSoon(): void {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    if (this.closed || this.reconnectTimer) return;
    const delay = Math.min(SSE_MAX_BACKOFF_MS, 1000 * 2 ** this.retry) + Math.random() * 500;
    this.retry++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.staleTimer) clearInterval(this.staleTimer);
    this.es?.close();
    this.es = null;
  }
}

export class Client {
  readonly clientId: string;
  private iceCache: RTCIceServer[] | null = null;

  constructor(
    readonly baseUrl: string,
    readonly token: string,
    clientId?: string,
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.clientId = clientId ?? "dc_" + Math.random().toString(36).slice(2, 12);
  }

  private headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: "Bearer " + this.token,
      "X-Client-Id": this.clientId,
    };
  }

  async get<T>(path: string): Promise<T> {
    const r = await fetch(this.baseUrl + path, { headers: this.headers() });
    if (!r.ok) throw new Error(`${path} ${r.status}`);
    return (await r.json()) as T;
  }

  async post<T>(path: string, body: unknown = {}): Promise<T> {
    const r = await fetch(this.baseUrl + path, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`${path} ${r.status}`);
    if (r.status === 204) return undefined as T;
    return (await r.json()) as T;
  }

  async del(path: string): Promise<void> {
    const r = await fetch(this.baseUrl + path, { method: "DELETE", headers: this.headers() });
    if (!r.ok && r.status !== 204) throw new Error(`${path} ${r.status}`);
  }

  // events opens the SSE stream for this device token. EventSource can't send
  // headers, so the token goes in the query (the backend reads ?token=). The
  // returned handle reconnects with backoff and detects half-open connections;
  // call close() to stop it.
  events(onEvent: (ev: BrokerEvent) => void): StreamHandle {
    const u = new URL(this.baseUrl + "/api/events");
    u.searchParams.set("token", this.token);
    u.searchParams.set("clientId", this.clientId);
    return new ReconnectingStream(u.toString(), onEvent);
  }

  async iceServers(): Promise<RTCIceServer[]> {
    if (this.iceCache) return this.iceCache;
    try {
      const { iceServers } = await this.get<{ iceServers: RTCIceServer[] }>("/api/webrtc-config");
      this.iceCache = iceServers?.length ? iceServers : FALLBACK_ICE;
    } catch {
      this.iceCache = FALLBACK_ICE;
    }
    return this.iceCache;
  }
}
